<script setup lang="ts">
/**
 * Step 2 — Position holders (directors / secretary / shareholders / auditor / CEO / MD).
 * Merged by ID number; editable list. Uses the Setup-Entity SG shared fields
 * (B03 id type, B04 nationality, date picker, B05 mobile, searchable address) so the
 * UI matches Setup Entity. Values stay as base-codes; converted at payload build.
 */
import { ref, watch } from 'vue';
import type { EfPositionHolder } from '../composables/incorporationEfilingModel';
import { createEmptySgAddress } from '@/features/setupEntity/components/sg/utils/sgLocalAddress';
import SgIdTypeField from '@/features/setupEntity/components/sg/shared/SgIdTypeField.vue';
import SgB04NationalityField from '@/features/setupEntity/components/sg/shared/SgB04NationalityField.vue';
import SgJurisdictionDateField from '@/features/setupEntity/components/sg/shared/SgJurisdictionDateField.vue';
import SgMobilePhoneFields from '@/features/setupEntity/components/sg/shared/SgMobilePhoneFields.vue';
import SgAddressFields from '@/features/setupEntity/components/sg/shared/SgAddressFields.vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';

const props = defineProps<{
  holders: EfPositionHolder[];
  readonly?: boolean;
  /** 밸리데이션 포커스: 이 인덱스의 아코디언을 강제로 연다. */
  openIndex?: number | null;
  /** 기본정보(주소·전화·이메일) 누락 하이라이트 대상 holder 인덱스. */
  contactErrorIndex?: number | null;
}>();

/** 주소에 내용이 하나라도 있는지(로컬/외국 라인 포함). */
function addrHasContent(a?: EfPositionHolder['contactAddress']): boolean {
  if (!a) return false;
  const v = a as unknown as Record<string, unknown>;
  return [
    'postalCode',
    'foreignAddressLine1',
    'streetName',
    'blockNo',
    'buildingName',
  ].some((k) => !!String(v[k] ?? '').trim());
}
/** contactErrorIndex 로 지정된 holder 의 특정 필드가 비었는지. */
function emailMissing(h: EfPositionHolder, i: number): boolean {
  return props.contactErrorIndex === i && !String(h.email || '').trim();
}
function phoneMissing(h: EfPositionHolder, i: number): boolean {
  return props.contactErrorIndex === i && !String(h.mobileNo || '').trim();
}
function addrMissing(h: EfPositionHolder, i: number): boolean {
  return props.contactErrorIndex === i && !addrHasContent(h.contactAddress);
}

// 아코디언 열림 상태(제어형) — 기본 전부 열림 유지, openIndex 지정 시 해당 패널을 확실히 연다.
const openPanels = ref<number[]>([]);
watch(
  () => props.openIndex,
  (idx) => {
    if (typeof idx === 'number' && idx >= 0 && !openPanels.value.includes(idx)) {
      openPanels.value = [...openPanels.value, idx];
    }
  },
);

const POSITIONS = [
  { value: 'LC_DIR', label: 'Director' },
  { value: 'LC_SEC', label: 'Secretary' },
  { value: 'LC_AUD', label: 'Auditor' },
  { value: 'LC_SH', label: 'Shareholder' },
  { value: 'LC_MGR', label: 'Managing Director' },
  { value: 'LC_CEO', label: 'CEO' },
];

function sourceLabel(h: EfPositionHolder): string {
  switch (h.source?.kind) {
    case 'director':
      return 'Director';
    case 'secretary':
      return 'Secretary';
    case 'shareholder':
      return 'Shareholder';
    case 'other':
      return 'Other holder';
    default:
      return 'Manual';
  }
}

function addHolder() {
  props.holders.push({
    category: '1',
    positions: ['LC_DIR'],
    regNo: '',
    idType: '',
    name: '',
    email: '',
    mobileCountryCode: '',
    mobileAreaCode: '',
    mobileNo: '',
    areaCodeNotApplicable: true,
    nationality: '',
    birthDate: '',
    contactAddress: createEmptySgAddress(),
    residentialAddress: createEmptySgAddress(),
  });
}

function removeHolder(i: number) {
  props.holders.splice(i, 1);
}
</script>

<template>
  <div class="ef-step">
    <p class="ef-step__desc">
      Every person to be filed with ACRA. Auto-filled from the setup-entity data — review positions,
      IDs and contact details before submission.
    </p>

    <div v-if="!holders.length" class="ef-step__empty">No position holders. Add one below.</div>

    <v-expansion-panels v-else v-model="openPanels" multiple variant="accordion" class="mb-3">
      <v-expansion-panel v-for="(h, i) in holders" :key="i">
        <v-expansion-panel-title>
          <div class="ph-title">
            <span class="ph-title__name">{{ h.name || '(no name)' }}</span>
            <span class="ph-title__meta">
              {{ h.category === '2' ? 'Corporate' : 'Individual' }} · {{ sourceLabel(h) }}
              <template v-if="h.positions.length"> · {{ h.positions.join(', ') }}</template>
            </span>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="h.category"
                :items="[
                  { title: 'Individual (Director/Secretary/Shareholder)', value: '1' },
                  { title: 'Corporate (Auditor / corporate shareholder)', value: '2' },
                ]"
                label="Category"
                density="comfortable"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col v-if="h.category === '1'" cols="12" sm="6">
              <SgIdTypeField v-model="h.idType" :readonly="readonly" required />
            </v-col>
          </v-row>

          <div class="ef-step__sub">Positions held</div>
          <div class="ph-positions">
            <v-checkbox
              v-for="p in POSITIONS"
              :key="p.value"
              v-model="h.positions"
              :label="p.label"
              :value="p.value"
              density="compact"
              hide-details
              :disabled="readonly"
              class="ph-positions__item"
            />
          </div>

          <v-row dense>
            <v-col
              v-if="!(h.category === '2' && (h.registeredLocally ?? 'YES') === 'NO')"
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="h.regNo"
                :label="h.category === '2' ? 'Entity UEN' : 'Registration ID No'"
                density="comfortable"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="h.name"
                label="Name"
                density="comfortable"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
          </v-row>

          <template v-if="h.category === '1'">
            <v-row dense>
              <v-col cols="12" sm="6">
                <SgB04NationalityField v-model="h.nationality" :readonly="readonly" />
              </v-col>
              <v-col cols="12" sm="6">
                <SgJurisdictionDateField
                  v-model="h.birthDate"
                  label="Birth date"
                  :readonly="readonly"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="h.email"
                  label="Email"
                  density="comfortable"
                  variant="outlined"
                  :error="emailMissing(h, i)"
                  :error-messages="emailMissing(h, i) ? 'Email is required' : []"
                  :hide-details="!emailMissing(h, i)"
                  :readonly="readonly"
                />
              </v-col>
            </v-row>

            <div class="ef-step__sub">Mobile</div>
            <div :class="{ 'ef-missing-outline': phoneMissing(h, i) }">
              <SgMobilePhoneFields
                v-model:country-code="h.mobileCountryCode"
                v-model:area-code="h.mobileAreaCode"
                v-model:mobile-no="h.mobileNo"
                v-model:area-code-not-applicable="h.areaCodeNotApplicable"
                :readonly="readonly"
                show-area-code-toggle
              />
              <div v-if="phoneMissing(h, i)" class="ef-missing-msg">Mobile number is required</div>
            </div>

            <div class="ef-step__sub">Contact address</div>
            <div :class="{ 'ef-missing-outline': addrMissing(h, i) }">
              <SgAddressFields
                :model-value="h.contactAddress"
                :readonly="readonly"
                show-level-unit-toggle
                @update:model-value="h.contactAddress = $event"
              />
              <div v-if="addrMissing(h, i)" class="ef-missing-msg">Contact address is required</div>
            </div>
          </template>

          <!-- 법인(category '2') — 로컬(SG) 등록 여부 + 로컬 아님이면 외국 법인 정보 -->
          <template v-else>
            <div class="ef-step__sub">Is the corporate position holder registered locally?</div>
            <v-radio-group
              :model-value="h.registeredLocally ?? 'YES'"
              inline
              density="compact"
              hide-details
              :readonly="readonly"
              @update:model-value="h.registeredLocally = $event === 'NO' ? 'NO' : 'YES'"
            >
              <v-radio label="Yes (local — SG)" value="YES" />
              <v-radio label="No (foreign)" value="NO" />
            </v-radio-group>

            <template v-if="(h.registeredLocally ?? 'YES') === 'NO'">
              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <CmBaseCodeAutocomplete
                    v-model="h.country"
                    main-code="B15"
                    label="Country / region of incorporation"
                    :read-only="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="h.fbrn"
                    label="Foreign business registration number (FBRN)"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="h.email"
                    label="Email"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
              </v-row>

              <div class="ef-step__sub">Foreign address</div>
              <SgAddressFields
                :model-value="h.contactAddress"
                :readonly="readonly"
                @update:model-value="h.contactAddress = $event"
              />
            </template>
          </template>

          <div v-if="!readonly" class="ph-actions">
            <v-btn
              variant="text"
              color="error"
              size="small"
              class="text-none"
              @click="removeHolder(i)"
            >
              Remove
            </v-btn>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-btn
      v-if="!readonly"
      variant="tonal"
      color="primary"
      size="small"
      class="text-none"
      prepend-icon="mdi-plus"
      @click="addHolder"
    >
      Add position holder
    </v-btn>
  </div>
</template>

<style scoped>
.ef-step__desc {
  font-size: 12.5px;
  color: #6b7a94;
  margin-bottom: 12px;
}
.ef-step__empty {
  font-size: 12.5px;
  color: #8a95a8;
  padding: 8px 0;
}
.ef-step__sub {
  font-size: 12.5px;
  font-weight: 700;
  color: #42506a;
  margin: 12px 0 6px;
}
.ph-title__name {
  font-weight: 700;
  margin-right: 8px;
}
.ph-title__meta {
  font-size: 12px;
  color: #6b7a94;
}
.ph-positions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-bottom: 6px;
}
.ph-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
/* 기본정보 누락 하이라이트(합성 컴포넌트: 전화/주소) */
.ef-missing-outline {
  border: 1px solid rgb(var(--v-theme-error, 176, 0, 32));
  border-radius: 6px;
  padding: 8px;
}
.ef-missing-msg {
  color: rgb(var(--v-theme-error, 176, 0, 32));
  font-size: 12px;
  margin-top: 4px;
}
</style>
